/**
 * Editor del prompt de narrativa IA por compañía.
 * Permite al admin escribir un "system message" custom que la IA usará al
 * redactar "Causas y circunstancias" para siniestros de esta compañía.
 * Si se deja vacío, la compañía vuelve a usar los defaults hardcoded.
 */
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sparkles, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export function NarrativePromptEditor({ companyId, companyName, companySlug }) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [initial, setInitial] = useState("");
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [usesDefault, setUsesDefault] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: cur }, { data: defs }] = await Promise.all([
        api.get(`/companies/${companyId}/narrative-prompt`),
        api.get(`/narrative-prompts/defaults`),
      ]);
      const p = cur.narrative_prompt || "";
      setPrompt(p);
      setInitial(p);
      setUsesDefault(!!cur.uses_default);
      setDefaults(defs);
    } catch (e) {
      toast.error("No se pudo cargar el prompt: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const loadBase = (which) => {
    if (!defaults) return;
    if (!prompt || window.confirm("Esto reemplazará el contenido actual del editor. ¿Continuar?")) {
      setPrompt(defaults[which] || "");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/companies/${companyId}/narrative-prompt`, { narrative_prompt: prompt });
      toast.success(prompt.trim() ? "Prompt guardado" : "Prompt eliminado (vuelve al default)");
      setInitial(prompt);
      setUsesDefault(!prompt.trim());
    } catch (e) {
      toast.error("Error al guardar: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const restoreDefault = async () => {
    if (!window.confirm(`Vas a eliminar el prompt custom de "${companyName}". La compañía volverá a usar el prompt por defecto del sistema. ¿Continuar?`)) return;
    setPrompt("");
    setSaving(true);
    try {
      await api.put(`/companies/${companyId}/narrative-prompt`, { narrative_prompt: null });
      toast.success("Prompt eliminado. Vuelve al default del sistema.");
      setInitial("");
      setUsesDefault(true);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const dirty = prompt !== initial;
  const charCount = prompt.length;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="rounded-[10px] border-[#E5E2D8]"
        data-testid={`company-narrative-prompt-btn-${companySlug}`}
      >
        <Sparkles className="h-3.5 w-3.5 mr-1.5 text-[#D85A30]" />
        {usesDefault ? "Configurar prompt IA…" : "Editar prompt IA (custom)"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-[16px] max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="narrative-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Prompt de narrativa IA — {companyName}
            </DialogTitle>
            <DialogDescription>
              Define cómo redacta la IA el apartado &ldquo;Causas y circunstancias&rdquo; para siniestros de esta compañía.
              Si lo dejas vacío, se usará la plantilla por defecto del sistema.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="py-12 flex items-center justify-center text-[#7A786F]">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Cargando…
            </div>
          ) : (
            <div className="flex flex-col gap-3 flex-1 overflow-hidden">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-[#7A786F]">Cargar plantilla base:</span>
                <Select onValueChange={loadBase}>
                  <SelectTrigger className="w-56 h-8 text-xs" data-testid="narrative-prompt-template-select">
                    <SelectValue placeholder="Elige una plantilla…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="videoperitacion">Videoperitación (default)</SelectItem>
                    <SelectItem value="telematico">Telemático (default)</SelectItem>
                    <SelectItem value="ocaso">Ocaso — PRD (700 chars/bloque)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="System message completo para Claude Sonnet 4.5. Ejemplo: 'Eres un perito de seguros de Mapfre. Sigue estas reglas… Formato de apertura obligatorio: …'"
                maxLength={20000}
                className="flex-1 min-h-[300px] font-mono text-xs bg-[#FBFAF6] text-[#2C2C2A] p-3 rounded-[10px] border border-[#E5E2D8] focus:border-[#D85A30] outline-none resize-none"
                data-testid="narrative-prompt-textarea"
              />
              <div className="flex items-center justify-between text-xs">
                <span className={`text-[#7A786F] ${charCount > 18000 ? "text-[#B43F3F]" : ""}`}>
                  {charCount.toLocaleString("es-ES")} / 20 000 caracteres
                </span>
                <span className="text-[#7A786F]">
                  {usesDefault && !prompt.trim() ? "Estado: usando plantilla por defecto" : "Estado: prompt custom activo"}
                </span>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={restoreDefault}
              disabled={saving || usesDefault}
              className="rounded-[10px] border-[#E5E2D8]"
              data-testid="narrative-prompt-restore"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Restaurar default
            </Button>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={saving}
              className="rounded-[10px] border-[#E5E2D8]"
              data-testid="narrative-prompt-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={save}
              disabled={saving || !dirty}
              className="bg-[#D85A30] hover:bg-[#C04D27] text-white rounded-[10px]"
              data-testid="narrative-prompt-save"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
